/**
 * Transient, platform-wide UI state: notifications, and the open/closed state
 * of overlays.
 *
 * Every module reports success and failure through this one notifier, so a
 * failed save looks the same in accounting as in HR — including the list of
 * specific reasons the server gave, which a generic toast would drop.
 */

import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Transient UI state
// ---------------------------------------------------------------------------

export interface Toast {
  id: number;
  tone: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  details?: string[];
  /*
   * Something the notice itself can do — in practice, undo.
   *
   * A message saying a shop was deleted is of no use to somebody who has just
   * realised they deleted the wrong one: by the time they have found the bin,
   * they are not sure what it was called. The way back belongs on the notice,
   * next to the thing that happened.
   */
  action?: { label: string; run: () => void | Promise<void> };
  /** How long it stays. An undo needs longer than a confirmation. */
  ttlMs?: number;
}

interface UIState {
  sidebarCollapsed: boolean;
  paletteOpen: boolean;
  searchOpen: boolean;
  quickCreateOpen: boolean;
  theme: 'light' | 'dark';
  toasts: Toast[];

  toggleSidebar: () => void;
  setPalette: (open: boolean) => void;
  setSearch: (open: boolean) => void;
  setQuickCreate: (open: boolean) => void;
  toggleTheme: () => void;
  notify: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: number) => void;
}

let toastSeq = 0;

export const useUI = create<UIState>((set, get) => ({
  sidebarCollapsed: (localStorage.getItem('rsc.sidebar') ?? localStorage.getItem('ledgerly.sidebar')) === 'collapsed',
  paletteOpen: false,
  searchOpen: false,
  quickCreateOpen: false,
  theme: ((localStorage.getItem('rsc.theme') ?? localStorage.getItem('ledgerly.theme')) as 'light' | 'dark') || 'light',
  toasts: [],

  toggleSidebar() {
    const next = !get().sidebarCollapsed;
    localStorage.setItem('rsc.sidebar', next ? 'collapsed' : 'expanded');
    set({ sidebarCollapsed: next });
  },
  setPalette: (paletteOpen) => set({ paletteOpen }),
  setSearch: (searchOpen) => set({ searchOpen }),
  setQuickCreate: (quickCreateOpen) => set({ quickCreateOpen }),

  toggleTheme() {
    const next = get().theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('rsc.theme', next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    set({ theme: next });
  },

  notify(t) {
    const id = ++toastSeq;
    set({ toasts: [...get().toasts, { ...t, id }] });
    // An undo needs long enough to notice the mistake and reach for it.
    const ttl = t.ttlMs ?? (t.action ? 12_000 : t.tone === 'error' ? 9000 : 4500);
    setTimeout(() => get().dismiss(id), ttl);
  },

  dismiss(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));

/** Consistent error reporting for any failed request. */
/** A server detail as one readable line: plain strings, or { field, message } objects. */
export function detailText(d: unknown): string {
  if (d === null || d === undefined) return '';
  if (typeof d === 'string') return d;
  if (typeof d === 'object') {
    const o = d as Record<string, unknown>;
    const message = typeof o.message === 'string' ? o.message : null;
    if (message) return typeof o.row === 'number' ? `Row ${o.row}: ${message}` : message;
  }
  try { return JSON.stringify(d); } catch { return String(d); }
}

export function reportError(err: any, fallback = 'Something went wrong') {
  const raw = err?.details?.errors
    || (err?.details?.dependencies || []).map((d: any) => d.message)
    || undefined;
  const details = Array.isArray(raw) ? raw.map(detailText).filter(Boolean) : undefined;
  useUI.getState().notify({
    tone: 'error',
    title: detailText(err?.message) || fallback,
    message: err?.code === 'PERIOD_LOCKED' ? 'This period is frozen.'
      : err?.code === 'ACCOUNTING_RULE' ? 'The entry breaks an accounting rule.'
        : undefined,
    details: Array.isArray(details) && details.length ? details.slice(0, 6) : undefined,
  });
}

export const notifySuccess = (title: string, message?: string) =>
  useUI.getState().notify({ tone: 'success', title, message });

/** For something that succeeded but the user still needs to know about. */
export const notifyWarning = (title: string, message?: string, details?: string[]) =>
  useUI.getState().notify({ tone: 'warning', title, message, details });
